// The HTTP client, built from ports.
//
// Constructed by each app's composition root rather than imported as a
// singleton: a module-level instance would have to be configured before any
// import touched it, which import hoisting makes fragile. Passing it through
// context is duller and works.

import axios, { type AxiosInstance } from 'axios';
import type { AuthPort, ConfigPort } from './ports';

export interface PortalClientDeps {
  config: ConfigPort;
  auth: Pick<AuthPort, 'getIdToken'>;
  /** Which linked tenant requests act in. Null until one is chosen. */
  getActiveTenantId: () => string | null;
  /** Called when the server rejects the session outright. */
  onUnauthorized?: () => void;
}

/** Codes meaning the session is fine but the tenant is not. */
export const TENANT_SELECTION_REQUIRED = 'TENANT_SELECTION_REQUIRED';
export const TENANT_LINK_UNCONFIRMED = 'TENANT_LINK_UNCONFIRMED';
export const TENANT_NOT_LINKED = 'TENANT_NOT_LINKED';

const TENANT_CODES: readonly string[] = [
  TENANT_SELECTION_REQUIRED,
  TENANT_LINK_UNCONFIRMED,
  TENANT_NOT_LINKED,
];

export function tenantErrorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { error?: { code?: string } } } })?.response?.data
    ?.error?.code;
  return code && TENANT_CODES.includes(code) ? code : null;
}

export function createHttpClient(deps: PortalClientDeps): AxiosInstance {
  const http = axios.create({
    baseURL: deps.config.apiBaseUrl,
    headers: { 'Content-Type': 'application/json' },
  });

  // Async on purpose: the auth SDK may need to refresh an expiring token before
  // it can hand one over, which is the whole reason sessions outlive an hour.
  http.interceptors.request.use(async config => {
    const token = await deps.auth.getIdToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    const tenantId = deps.getActiveTenantId();
    if (tenantId) config.headers['X-Tenant-Id'] = tenantId;

    return config;
  });

  http.interceptors.response.use(
    response => response,
    error => {
      // A tenant error is not a dead session — the caller resolves it by
      // choosing or confirming a tenant. Signing out here would eject a
      // perfectly valid login.
      if (error?.response?.status === 401 && !tenantErrorCode(error)) {
        deps.onUnauthorized?.();
      }
      return Promise.reject(error);
    }
  );

  return http;
}
